import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../../../../components/Loader";
import { calculateEndDateTime } from "../../../../utils/eventCalender";
import { getUserRole } from "../../../../store/userRole";
import { useSelector } from "react-redux";

// Custom hooks
import useClassroomData from "./hooks/useClassroomData";
import useLiveClassData from "./hooks/useLiveClassData";
import useLiveClassFilters from "./hooks/useLiveClassFilters";

// Components
import LiveClassHeader from "./components/LiveClassHeader";
import LiveClassTabs from "./components/LiveClassTabs";
import LiveClassFooter from "./components/LiveClassFooter";
import LiveClassModals from "./components/modals/LiveClassModals";
import "./components/styles.scss";

const LiveClasses = ({ classroomId: classId = "", isMobileView }) => {
  const navigate = useNavigate();
  const { id: paramsClassId } = useParams();
  const { id } = useParams();
  const roleOfTheUser = useSelector(getUserRole);
  const isStudent = roleOfTheUser === "Student";
  

  // Modals state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openSuspendModal, setOpenSuspendModal] = useState(false);
  const [openReschedule, setOpenReschedule] = useState(false);
  const [selectedLiveClass, setSelectedLiveClass] = useState({});
  const [rowId, setRowId] = useState("");
  const [shareLink, setShareLink] = useState();
  const [openImportModal, setOpenImportModal] = useState(false)

  // Custom hooks
  const {
    classroomId,
    classroomLoader,
    classroomEndDate,
    permissions,
    createdBy,
    userInformation,
    subjectList,
    classUsers,
  } = useClassroomData(classId, paramsClassId);
  // Modify permissions for students
  const effectivePermissions = isStudent
    ? {
        view: true,
        join: true,
        viewRecordings: true,
        delete: false,
        suspend: false,
        reschedule: false,
        share: false,
        edit: false,
      }
    : permissions;

  const {
    liveClassData,
    upcomingClassData,
    completedClassData,
    loading,
    liveLoader,
    upComingLoader,
    completedLoader,
    noOfLivePages,
    pageOfLive,
    noOfUpcomingPages,
    pageOfUpcoming,
    noOfCompletedPages,
    pageOfCompleted,
    startLiveClass,
    removeLiveClass,
    deleteLiveClassHandler,
    suspendLiveClass,
    resetFilters,
    applyFilters,
    loadMoreLiveClasses,
    loadMoreUpcomingClasses,
    loadMoreCompletedClasses,
    appliedFilters,
    completedCount,
    upcomingCount,
    refreshCompletedOnes,
  } = useLiveClassData(classroomId, id);

  const {
    loading: filterLoading,
    filterOptions,
    selectedFilters,
    setSelectedFilters,
  } = useLiveClassFilters();

  // Event handlers
  const handleDeleteModal = (id) => {
    setOpenDeleteModal(true);
    setRowId(id);
  };

  const handleDeleteSubmit = () => {
    deleteLiveClassHandler(rowId);
    setOpenDeleteModal(false);
  };

  const handleSuspendSubmit = async () => {
    const success = await suspendLiveClass(selectedLiveClass);
    if (success) {
      setSelectedLiveClass({});
      setOpenSuspendModal(false);
    }
  };

  const handleSuspend = (liveClassId) => {
    const selectUpcoming = upcomingClassData.find(
      (liveClass) => liveClass?._id === liveClassId
    );
    setOpenSuspendModal(true);
    setSelectedLiveClass(selectUpcoming);
  };

  const handleReschedule = (liveClassId) => {
    const selectUpcoming = upcomingClassData.find(
      (liveClass) => liveClass?._id === liveClassId
    );
    selectUpcoming.start = new Date(selectUpcoming?.startDateTime);
    selectUpcoming.end = new Date(
      calculateEndDateTime(
        selectUpcoming?.startDateTime,
        selectUpcoming?.duration
      )
    );
    setOpenReschedule(true);
    setSelectedLiveClass(selectUpcoming);
  };

  const handleEditClass = (id) => {
    navigate(
      `/classrooms/${id}/create-single-live-class?classroom=${classroomId}&classDetailsPage=true`
    );
  };

  const handleShareLiveClass = (liveclassid) => {
    const link = `${window.location.origin}/register?classroomId=${classroomId}&liveclassid=${liveclassid}`;
    setShareLink(link);
    setOpenShareModal(true);
  };

  const handlePlayRecordings = (liveclassid) => {
    const extraPath = isStudent ? "/student" : "";
    if (classId === "") {
      navigate(
        `${extraPath}/myClassrooms/${classroomId}/recordings?liveclass=${liveclassid}`
      );
    } else {
      navigate(
        `${extraPath}/liveclasses/${classroomId}/recordings?liveclass=${liveclassid}`
      );
    }
  };



  // Show loader if any data is loading
  if (
    loading ||
    liveLoader ||
    upComingLoader ||
    classroomLoader ||
    filterLoading
  ) {
    return <Loader />;
  }

  const getFilterOptionsList = () => {
    const classroomUsers = classUsers.map((user) => user?.user);
    const validUserOptions = filterOptions?.teachers?.filter((user) =>
      classroomUsers?.includes(user?.id)
    );
    return {
      ...filterOptions,
      teachers: validUserOptions,
      subjects: subjectList,
    };
  };

  return (
    <div
      className="add-live-classes-styles"
      style={{ paddingBottom: "40px", paddingTop: "0px" }}
    >
      {completedLoader && <Loader />}
      {/* Modals - Only show for non-students */}
      {!isStudent && (
        <LiveClassModals
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          onDeleteSubmit={handleDeleteSubmit}
          openShareModal={openShareModal}
          setOpenShareModal={setOpenShareModal}
          shareLink={shareLink}
          openSuspendModal={openSuspendModal}
          setOpenSuspendModal={setOpenSuspendModal}
          onSuspendSubmit={handleSuspendSubmit}
          selectedLiveClass={selectedLiveClass}
          setSelectedLiveClass={setSelectedLiveClass}
          openReschedule={openReschedule}
          setOpenReschedule={setOpenReschedule}
          onRescheduleUpdate={resetFilters}
          classroomEndDate={classroomEndDate}
        />
      )}

      {/* Header with filters - Only show for non-students */}
      {!isStudent && (
        <LiveClassHeader
          id={id}
          classId={classId}
          permissions={effectivePermissions}
          filterOptions={{ ...getFilterOptionsList() }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          resetFilters={resetFilters}
          applyFilters={applyFilters}
          appliedFilters={appliedFilters}
          openImportModal={openImportModal}
          setOpenImportModal={setOpenImportModal}
          isMobileView={isMobileView}
          refreshCompletedOnes={refreshCompletedOnes}
        />
      )}

      {/* Tabbed interface */}
      <div className={isMobileView ? "" : "px-4"}>
        <LiveClassTabs
          isMobileView={isMobileView}
          liveClassData={liveClassData}
          upcomingClassData={upcomingClassData}
          completedClassData={completedClassData}
          noOfLivePages={noOfLivePages}
          pageOfLive={pageOfLive}
          loadMoreLiveClasses={loadMoreLiveClasses}
          noOfUpcomingPages={noOfUpcomingPages}
          pageOfUpcoming={pageOfUpcoming}
          loadMoreUpcomingClasses={loadMoreUpcomingClasses}
          noOfCompletedPages={noOfCompletedPages}
          pageOfCompleted={pageOfCompleted}
          loadMoreCompletedClasses={loadMoreCompletedClasses}
          handleStartClass={(id, meetingId, pw, key) =>
            startLiveClass(id, meetingId, pw, key)
          }
          handleEndClass={(id, meetingId, pw) =>
            removeLiveClass(id, meetingId, pw)
          }
          deleteModalhandler={handleDeleteModal}
          editHandler={handleEditClass}
          shareModalHandler={handleShareLiveClass}
          onSuspendHandler={handleSuspend}
          onRescheduleHandler={handleReschedule}
          playRecordings={handlePlayRecordings}
          userId={userInformation?._id}
          permissions={effectivePermissions}
          isStudent={isStudent}
          showThreeDots={!isStudent}
          appliedFilters={appliedFilters}
          completedCount={completedCount}
          upcomingCount={upcomingCount}
        />
      </div>

      {/* Footer buttons - Only show for non-students */}
      {!isStudent && (
        <LiveClassFooter
          classId={classId}
          classroomId={classroomId}
          permissions={effectivePermissions}
          createdBy={createdBy}
          userInformation={userInformation}
          navigate={navigate}
        />
      )}
    </div>
  );
};

export default LiveClasses;
